import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSearch,
  faTrophy,
  faTrash,
  faStop,
  faColumns,
  faEraser,
  faUserSlash,
  faUserCheck,
  faEye,
  faEyeSlash,
  faFileExcel,
  faPlus,
  faPen,
  faLayerGroup,
  faExpand,
  faCompress,
  faUsers,
  faImage
} from '@fortawesome/free-solid-svg-icons';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';
import BoardResultsPosters from './BoardResultsPosters';
import BoardResultsPosterPreview from './BoardResultsPosterPreview';
import { useAuth } from '../contexts/AuthContext';
import { exportBoardResultsExcel } from '../utils/exportBoardResultsExcel';
import './BoardResultsTab.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR - i);
const COL_VIS_KEY = 'boardResults_hiddenColumns';
const SECTION_SEL_KEY = 'boardResults_selectedSection';
const COMBINE_SECTIONS_KEY = 'boardResults_combineSections';
const GROUP_SEL_KEY = 'boardResults_groupKind';
const VIEW_MODE_KEY = 'boardResults_viewMode';
const STATS_SCOPE_KEY = 'boardResults_statsScope';
const BISE_SOURCE_KEY = 'boardResults_biseSource';
const COMBINED_ID = 'combined';
/** Fixed captcha solver — bartowski/ath-maas_ovisocr2 (dropdown hidden). */
const CAPTCHA_MODEL = 'ovisocr2';
/** Official captcha site (photos) vs www JSON API (no captcha). */
const BISE_SOURCES = [
  {
    key: 'result',
    label: 'result.biselahore.com',
    title: 'Official site — captcha OCR; Matric & Intermediate; Part-I & Part-II; includes student photo'
  },
  {
    key: 'www',
    label: 'biselahore.com',
    title: 'JSON API when open; auto-falls back to result.biselahore.com captcha if blocked (no photo on JSON)'
  }
];

/** Matches backend wwwSourceSupported — datasets published on result-data.biselahore.com */
function wwwSourceSupported(lvl, examPart) {
  if (lvl === 'Intermediate' && examPart === 'Part-II') return true;
  if (lvl === 'Matric' && examPart === 'Part-I') return true;
  return false;
}

function loadSavedBiseSource() {
  try {
    const v = localStorage.getItem(BISE_SOURCE_KEY);
    if (v === 'result' || v === 'www') return v;
  } catch { /* ignore */ }
  return 'result';
}

function saveBiseSource(key) {
  try {
    localStorage.setItem(BISE_SOURCE_KEY, key);
  } catch { /* ignore */ }
}
const STATS_SCOPES = [
  { key: 'included', label: 'Included', title: 'Averages & ranks from included students only' },
  { key: 'excluded', label: 'Excluded', title: 'Averages & ranks from excluded students only' },
  { key: 'both', label: 'Both', title: 'Averages & ranks from included and excluded students' }
];

function loadSavedStatsScope() {
  try {
    const v = localStorage.getItem(STATS_SCOPE_KEY);
    if (v === 'included' || v === 'excluded' || v === 'both') return v;
  } catch { /* ignore */ }
  return 'included';
}

const SCIENCE_COLUMNS = [
  { key: 'rank_no', label: 'Rank' },
  { key: 'roll_no', label: 'Roll' },
  { key: 'student_name', label: 'Name' },
  { key: 'quran', label: 'QURAN' },
  { key: 'urdu', label: 'URDU' },
  { key: 'eng', label: 'ENG' },
  { key: 'isl', label: 'ISL' },
  { key: 'pst', label: 'P.St' },
  { key: 'math', label: 'Math' },
  { key: 'phy', label: 'Phy' },
  { key: 'chem', label: 'Chem' },
  { key: 'comp', label: 'Comp' },
  { key: 'bio', label: 'Bio' },
  { key: 'stat', label: 'Stat' },
  { key: 'part1_total', label: 'Total' },
  { key: 'column2', label: '' },
  { key: 'quran2', label: 'Quran2' },
  { key: 'urdu2', label: 'URDU2' },
  { key: 'english2', label: 'ENG2' },
  { key: 'isl2', label: 'ISL2' },
  { key: 'pst2', label: 'P.ST2' },
  { key: 'math2', label: 'MATH2' },
  { key: 'phy2', label: 'PHY2' },
  { key: 'chem2', label: 'CHEM2' },
  { key: 'comp2', label: 'COMP2' },
  { key: 'bio2', label: 'BIO2' },
  { key: 'stat2', label: 'STAT2' },
  { key: 'pr_physics', label: 'pr.ph' },
  { key: 'pr_chemistry', label: 'pr.ch' },
  { key: 'pr_computer', label: 'pr.co' },
  { key: 'pr_biology', label: 'pr.bio' },
  { key: 'pr_statistics', label: 'pr.st' },
  { key: 'marks_9th', label: '9th Marks' },
  { key: 'marks_10th', label: '10th Marks' },
  { key: 'marks_diff', label: '10th−9th' },
  { key: 'total_marks', label: 'Total Marks' },
  { key: 'percentage_label', label: '%' }
];

/** Humanities: Gen Math / Gen Science / Adv Islamic Studies (+ Computer practical). */
const HUMANITIES_COLUMNS = [
  { key: 'rank_no', label: 'Rank' },
  { key: 'roll_no', label: 'Roll' },
  { key: 'student_name', label: 'Name' },
  { key: 'quran', label: 'QURAN' },
  { key: 'urdu', label: 'URDU' },
  { key: 'eng', label: 'ENG' },
  { key: 'isl', label: 'ISL' },
  { key: 'pst', label: 'P.St' },
  { key: 'gen_math', label: 'G.Math' },
  { key: 'gen_sci', label: 'G.Sci' },
  { key: 'adv_isl', label: 'Adv.Isl' },
  { key: 'comp', label: 'Comp' },
  { key: 'part1_total', label: 'Total' },
  { key: 'column2', label: '' },
  { key: 'quran2', label: 'Quran2' },
  { key: 'urdu2', label: 'URDU2' },
  { key: 'english2', label: 'ENG2' },
  { key: 'isl2', label: 'ISL2' },
  { key: 'pst2', label: 'P.ST2' },
  { key: 'gen_math2', label: 'G.Math2' },
  { key: 'gen_sci2', label: 'G.Sci2' },
  { key: 'adv_isl2', label: 'Adv.Isl2' },
  { key: 'comp2', label: 'COMP2' },
  { key: 'pr_computer', label: 'pr.co' },
  { key: 'marks_9th', label: '9th Marks' },
  { key: 'marks_10th', label: '10th Marks' },
  { key: 'marks_diff', label: '10th−9th' },
  { key: 'total_marks', label: 'Total Marks' },
  { key: 'percentage_label', label: '%' }
];

const SCIENCE_SUBJECT_MAX = {
  quran: 50, urdu: 75, eng: 75, isl: 100, pst: 100,
  math: 75, phy: 60, chem: 60, comp: 50, bio: 60, stat: 100,
  quran2: 50, urdu2: 75, english2: 75, isl2: 100, pst2: 100,
  math2: 75, phy2: 60, chem2: 60, comp2: 50, bio2: 60, stat2: 100,
  pr_physics: 30, pr_chemistry: 30, pr_computer: 50, pr_biology: 30, pr_statistics: 50
};

/** Intermediate (class 11–12) theory max per part; combined total = 1200. */
const INTERMEDIATE_SCIENCE_SUBJECT_MAX = {
  quran: 50, urdu: 100, eng: 100, isl: 50, pst: 50,
  math: 100, phy: 85, chem: 85, comp: 75, bio: 85, stat: 100,
  quran2: 50, urdu2: 100, english2: 100, isl2: 50, pst2: 50,
  math2: 100, phy2: 85, chem2: 85, comp2: 75, bio2: 85, stat2: 100,
  pr_physics: 30, pr_chemistry: 30, pr_computer: 50, pr_biology: 30, pr_statistics: 0
};


const HUMANITIES_SUBJECT_MAX = {
  quran: 50, urdu: 75, eng: 75, isl: 100, pst: 100,
  gen_math: 75, gen_sci: 75, adv_isl: 75, comp: 50,
  quran2: 50, urdu2: 75, english2: 75, isl2: 100, pst2: 100,
  gen_math2: 75, gen_sci2: 75, adv_isl2: 75, comp2: 50,
  pr_computer: 50
};

const SCIENCE_CLASS9 = new Set([
  'quran', 'urdu', 'eng', 'isl', 'pst', 'math', 'phy', 'chem', 'comp', 'bio', 'stat'
]);
const SCIENCE_CLASS10 = new Set([
  'quran2', 'urdu2', 'english2', 'isl2', 'pst2', 'math2', 'phy2', 'chem2', 'comp2', 'bio2', 'stat2'
]);
const HUMANITIES_CLASS9 = new Set([
  'quran', 'urdu', 'eng', 'isl', 'pst', 'gen_math', 'gen_sci', 'adv_isl', 'comp'
]);
const HUMANITIES_CLASS10 = new Set([
  'quran2', 'urdu2', 'english2', 'isl2', 'pst2', 'gen_math2', 'gen_sci2', 'adv_isl2', 'comp2'
]);

/** Part-I (9th): default visible columns — rest start unchecked in the Columns menu. */
const SCIENCE_PART1_VISIBLE = new Set([
  'rank_no', 'roll_no', 'student_name',
  'quran', 'urdu', 'eng', 'isl', 'pst', 'math', 'phy', 'chem', 'comp', 'bio',
  'part1_total', 'total_marks', 'percentage_label'
]);
const HUMANITIES_PART1_VISIBLE = new Set([
  'rank_no', 'roll_no', 'student_name',
  'quran', 'urdu', 'eng', 'isl', 'pst', 'gen_math', 'gen_sci', 'adv_isl', 'comp',
  'part1_total', 'total_marks', 'percentage_label'
]);

const FAIL_PCT = 33.3;
const READONLY_KEYS = new Set([
  'rank_no', 'percentage_label', 'column2', 'section_name', 'roll_no', 'marks_diff'
]);

function computeMarksDiff(row) {
  const tenth = parseMark(row?.marks_10th);
  const ninth = parseMark(row?.marks_9th);
  if (tenth == null || ninth == null) return null;
  return tenth - ninth;
}

function formatMarksDiff(diff) {
  if (diff == null) return '';
  if (diff > 0) return `+${diff}`;
  return String(diff);
}

function cellValue(row, key) {
  if (key === 'marks_diff') return formatMarksDiff(computeMarksDiff(row));
  return row?.[key];
}

function compareSortValues(a, b, key) {
  // Numeric-ish keys (marks, ranks, %)
  if (key === 'marks_diff') {
    const da = computeMarksDiff(a);
    const db = computeMarksDiff(b);
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  }
  if (key === 'percentage_label' || key === 'percentage') {
    const pa = a.percentage != null ? Number(a.percentage) : parseMark(String(a.percentage_label || '').replace('%', ''));
    const pb = b.percentage != null ? Number(b.percentage) : parseMark(String(b.percentage_label || '').replace('%', ''));
    if (pa == null && pb == null) return 0;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa - pb;
  }
  if (key === 'rank_no') {
    const ra = a.rank_no != null ? Number(a.rank_no) : null;
    const rb = b.rank_no != null ? Number(b.rank_no) : null;
    if (ra == null && rb == null) return 0;
    if (ra == null) return 1;
    if (rb == null) return -1;
    return ra - rb;
  }

  const na = parseMark(a[key]);
  const nb = parseMark(b[key]);
  if (na != null || nb != null) {
    if (na == null && nb == null) return 0;
    if (na == null) return 1;
    if (nb == null) return -1;
    return na - nb;
  }

  const sa = String(a[key] ?? '').trim().toLowerCase();
  const sb = String(b[key] ?? '').trim().toLowerCase();
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
}

function sortRows(list, sortKey, sortDir) {
  if (!sortKey || !list?.length) return list;
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const cmp = compareSortValues(a, b, sortKey);
    if (cmp !== 0) return cmp * dir;
    // Stable-ish tie-breakers
    const nameCmp = String(a.student_name || '').localeCompare(String(b.student_name || ''));
    if (nameCmp !== 0) return nameCmp;
    return String(a.roll_no || '').localeCompare(String(b.roll_no || ''), undefined, { numeric: true });
  });
}

function buildColumns(isCombined, groupKind) {
  const base = groupKind === 'humanities' ? HUMANITIES_COLUMNS : SCIENCE_COLUMNS;
  if (!isCombined) return base;
  const cols = [];
  for (const c of base) {
    cols.push(c);
    if (c.key === 'student_name') {
      cols.push({ key: 'section_name', label: 'Section' });
    }
  }
  return cols;
}

/** Sticky-left freeze classes for Rank / Roll / Name (and checkbox via select-col). */
function freezeColClass(key) {
  if (key === 'rank_no') return 'col-freeze col-freeze-rank';
  if (key === 'roll_no') return 'col-freeze col-freeze-roll';
  if (key === 'student_name') return 'col-freeze col-freeze-name';
  return '';
}

function subjectMaxFor(groupKind, level) {
  if (level === 'Intermediate') {
    // Humanities Intermediate still uses science elective maxes for shared columns;
    // dedicated FA maxes can be added later when needed.
    return groupKind === 'humanities' ? HUMANITIES_SUBJECT_MAX : INTERMEDIATE_SCIENCE_SUBJECT_MAX;
  }
  return groupKind === 'humanities' ? HUMANITIES_SUBJECT_MAX : SCIENCE_SUBJECT_MAX;
}

function classKeysFor(groupKind) {
  if (groupKind === 'humanities') {
    return { class9: HUMANITIES_CLASS9, class10: HUMANITIES_CLASS10 };
  }
  return { class9: SCIENCE_CLASS9, class10: SCIENCE_CLASS10 };
}

function sectionStorageKey(year, level, part) {
  return `${SECTION_SEL_KEY}:${year}:${level}:${part}`;
}

function loadSavedSectionId(year, level, part) {
  try {
    return localStorage.getItem(sectionStorageKey(year, level, part)) || null;
  } catch {
    return null;
  }
}

function saveSelectedSectionId(year, level, part, id) {
  try {
    localStorage.setItem(sectionStorageKey(year, level, part), String(id));
  } catch {
    /* ignore */
  }
}

function combineStorageKey(year, level, part) {
  return `${COMBINE_SECTIONS_KEY}:${year}:${level}:${part}`;
}

function loadSavedCombineIds(year, level, part) {
  try {
    const raw = localStorage.getItem(combineStorageKey(year, level, part));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    return arr.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  } catch {
    return null;
  }
}

function saveCombineIds(year, level, part, ids) {
  try {
    localStorage.setItem(
      combineStorageKey(year, level, part),
      JSON.stringify([...ids].map(Number).filter((n) => Number.isFinite(n) && n > 0))
    );
  } catch {
    /* ignore */
  }
}

function loadSavedGroupKind() {
  try {
    const v = localStorage.getItem(GROUP_SEL_KEY);
    return v === 'humanities' ? 'humanities' : 'science';
  } catch {
    return 'science';
  }
}

function saveGroupKind(kind) {
  try {
    localStorage.setItem(GROUP_SEL_KEY, kind);
  } catch {
    /* ignore */
  }
}

function loadSavedViewMode() {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'posters' ? 'posters' : 'table';
  } catch {
    return 'table';
  }
}

function saveViewMode(mode) {
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode === 'posters' ? 'posters' : 'table');
  } catch {
    /* ignore */
  }
}

function isHumanities(groupName) {
  return /human/i.test(String(groupName || ''));
}

function parseMark(value) {
  if (value == null) return null;
  const t = String(value).trim();
  if (!t || t === '--' || t === '-' || t === '—') return null;
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

function subjectPct(value, key, subjectMax) {
  const max = subjectMax[key];
  const mark = parseMark(value);
  if (max == null || mark == null || max <= 0) return null;
  return (mark / max) * 100;
}

function isFailCell(value, key, subjectMax) {
  const pct = subjectPct(value, key, subjectMax);
  return pct != null && pct < FAIL_PCT;
}

function rankWithinGroup(avgs, keys) {
  const ranked = [...keys]
    .filter((key) => avgs[key] != null)
    .sort((a, b) => avgs[b] - avgs[a]);
  const ranks = {};
  ranked.forEach((key, i) => {
    ranks[key] = i + 1;
  });
  return ranks;
}

function computeSubjectStats(rows, subjectMax, class9Keys, class10Keys) {
  const avgs = {};
  for (const key of Object.keys(subjectMax)) {
    let sum = 0;
    let n = 0;
    for (const row of rows) {
      const mark = parseMark(row[key]);
      if (mark == null) continue;
      sum += mark;
      n += 1;
    }
    if (n === 0) {
      avgs[key] = null;
      continue;
    }
    const max = subjectMax[key];
    avgs[key] = (sum / n / max) * 100;
  }
  const ranks9 = rankWithinGroup(avgs, class9Keys);
  const ranks10 = rankWithinGroup(avgs, class10Keys);
  return { avgs, ranks: { ...ranks9, ...ranks10 }, ranks9, ranks10 };
}

function colVisStorageKey(groupKind, part) {
  const p = part === 'Part-I' ? 'Part-I' : 'Part-II';
  return `${COL_VIS_KEY}_${groupKind || 'science'}_${p}`;
}

function defaultHiddenColumns(groupKind, part, isCombined) {
  if (part !== 'Part-I') return new Set();
  const visible = new Set(
    groupKind === 'humanities' ? HUMANITIES_PART1_VISIBLE : SCIENCE_PART1_VISIBLE
  );
  if (isCombined) visible.add('section_name');
  const hidden = new Set();
  for (const c of buildColumns(isCombined, groupKind)) {
    if (!visible.has(c.key)) hidden.add(c.key);
  }
  return hidden;
}

function loadHiddenColumns(groupKind, part, isCombined = false) {
  try {
    const partNorm = part === 'Part-I' ? 'Part-I' : 'Part-II';
    let raw = localStorage.getItem(colVisStorageKey(groupKind, partNorm));
    if (raw == null && partNorm === 'Part-II') {
      raw = localStorage.getItem(`${COL_VIS_KEY}_${groupKind || 'science'}`);
    }
    if (raw != null) {
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    }
  } catch {
    /* ignore */
  }
  return defaultHiddenColumns(groupKind, part, isCombined);
}

function cellDisplay(v) {
  if (v == null || v === '') return '';
  return String(v);
}

/** Parse pasted rolls: newlines, commas, spaces, tabs, semicolons. */
function parseRollList(text) {
  const seen = new Set();
  const rolls = [];
  for (const part of String(text || '').split(/[\s,;]+/)) {
    const roll = part.replace(/\D/g, '').slice(0, 6);
    if (!/^\d{1,6}$/.test(roll)) continue;
    if (seen.has(roll)) continue;
    seen.add(roll);
    rolls.push(roll);
  }
  return rolls;
}

export default function BoardResultsTab() {
  const API_BASE_URL = window.API_BASE_URL;
  const { currentUser } = useAuth();
  // Deleting a section wipes every student row inside it, so it stays
  // an administrator-only action.
  const canDeleteSection = String(currentUser?.role || '').toLowerCase() === 'administrator';
  const stopRef = useRef(false);
  const columnsMenuRef = useRef(null);
  const tableCardRef = useRef(null);

  const [rollListText, setRollListText] = useState('');
  const [part, setPart] = useState('Part-II');
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [level, setLevel] = useState('Matric');
  const [biseSource, setBiseSource] = useState(loadSavedBiseSource);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [tableFullscreen, setTableFullscreen] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(null);
  const [batchLog, setBatchLog] = useState([]);
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    const gk = loadSavedGroupKind();
    return loadHiddenColumns(gk, 'Part-II', false);
  });
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sections, setSections] = useState([]);
  const [combinedMeta, setCombinedMeta] = useState({ id: COMBINED_ID, name: 'Combined', student_count: 0 });
  const [selectedSectionId, setSelectedSectionId] = useState(() => loadSavedSectionId(String(CURRENT_YEAR), 'Matric', 'Part-II') || COMBINED_ID);
  /** Section IDs included when Combined is active (multi-select merge). */
  const [combineSectionIds, setCombineSectionIds] = useState(() => {
    const saved = loadSavedCombineIds(String(CURRENT_YEAR), 'Matric', 'Part-II');
    return saved?.length ? new Set(saved) : new Set();
  });
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [groupKind, setGroupKind] = useState(loadSavedGroupKind);
  const [viewMode, setViewMode] = useState(loadSavedViewMode);
  const [sortKey, setSortKey] = useState('rank_no');
  const [sortDir, setSortDir] = useState('asc'); // asc | desc
  const [statsScope, setStatsScope] = useState(loadSavedStatsScope); // included | excluded | both
  const [posterPreviewRow, setPosterPreviewRow] = useState(null);

  const isCombined = String(selectedSectionId) === COMBINED_ID;
  const isHumanitiesView = groupKind === 'humanities';
  const isPostersView = viewMode === 'posters';
  const selectedSection = useMemo(
    () => (isCombined ? null : sections.find((s) => String(s.id) === String(selectedSectionId)) || null),
    [isCombined, sections, selectedSectionId]
  );
  const combineSections = useMemo(
    () => sections.filter((s) => combineSectionIds.has(Number(s.id))),
    [sections, combineSectionIds]
  );
  const combinedDisplayCount = useMemo(() => {
    if (!isCombined) return combinedMeta.student_count || 0;
    if (!combineSections.length) {
      return isHumanitiesView
        ? (combinedMeta.humanities_count ?? combinedMeta.student_count ?? 0)
        : (combinedMeta.science_count ?? combinedMeta.student_count ?? 0);
    }
    return combineSections.reduce(
      (n, s) => n + (isHumanitiesView ? (s.humanities_count || 0) : (s.science_count || 0)),
      0
    );
  }, [isCombined, combineSections, combinedMeta, isHumanitiesView]);
  const combinedNamesLabel = useMemo(() => {
    if (!combineSections.length) return 'All';
    return combineSections.map((s) => s.name).join('+');
  }, [combineSections]);
  const SUBJECT_MAX = useMemo(() => subjectMaxFor(groupKind, level), [groupKind, level]);
  const { class9: CLASS9_KEYS, class10: CLASS10_KEYS } = useMemo(
    () => classKeysFor(groupKind),
    [groupKind]
  );
  const COLUMNS = useMemo(() => buildColumns(isCombined, groupKind), [isCombined, groupKind]);

  const selectGroupKind = useCallback((kind) => {
    const next = kind === 'humanities' ? 'humanities' : 'science';
    setGroupKind(next);
    saveGroupKind(next);
    setHiddenColumns(loadHiddenColumns(next, part, isCombined));
    setSelectedIds(new Set());
  }, [part, isCombined]);

  const selectViewMode = useCallback((mode) => {
    const next = mode === 'posters' ? 'posters' : 'table';
    setViewMode(next);
    saveViewMode(next);
    if (next === 'posters') setSelectedIds(new Set());
  }, []);

  const showMessage = (text, type = 'info') => setMessage({ show: true, text, type });
  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirmOk = () => {
    const cb = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    if (cb) setTimeout(() => cb(), 50);
  };
  const handleConfirmCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const parsedRolls = useMemo(() => parseRollList(rollListText), [rollListText]);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => !hiddenColumns.has(c.key)),
    [hiddenColumns, COLUMNS]
  );

  const activeRows = useMemo(
    () => rows.filter((r) => !r.is_disabled),
    [rows]
  );

  const disabledRows = useMemo(
    () => rows.filter((r) => r.is_disabled),
    [rows]
  );

  /** Main table: active only, or only excluded when "Show excluded" is on */
  const displayRows = useMemo(() => {
    const base = showDisabled ? disabledRows : activeRows;
    return sortRows(base, sortKey, sortDir);
  }, [showDisabled, activeRows, disabledRows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (!key || key === 'column2') return;
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      // Default: text columns A→Z, numeric/rank high-first for marks/%/diff
      const numericDefaultDesc = ![
        'student_name', 'roll_no', 'section_name', 'rank_no'
      ].includes(key);
      setSortDir(numericDefaultDesc ? 'desc' : 'asc');
      return key;
    });
  };

  const displayIds = useMemo(
    () => displayRows.map((r) => r.id).filter(Boolean),
    [displayRows]
  );

  const selectedInView = useMemo(() => {
    let n = 0;
    for (const id of displayIds) {
      if (selectedIds.has(id)) n += 1;
    }
    return n;
  }, [displayIds, selectedIds]);

  const allSelectedInView =
    displayIds.length > 0 && selectedInView === displayIds.length;

  // Drop selection for rows that left the current view / filter
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(displayIds);
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [displayIds]);

  // Clear selection when switching included ↔ excluded / cohort / section / group
  useEffect(() => {
    setSelectedIds(new Set());
  }, [showDisabled, year, level, part, selectedSectionId, groupKind]);

  // Reset sort to rank when switching group/section/cohort
  useEffect(() => {
    setSortKey('rank_no');
    setSortDir('asc');
  }, [groupKind, selectedSectionId, year, level, part]);

  const statsRows = useMemo(() => {
    if (statsScope === 'excluded') return disabledRows;
    if (statsScope === 'both') return rows;
    return activeRows;
  }, [statsScope, activeRows, disabledRows, rows]);

  const statsScopeLabel = useMemo(() => {
    if (statsScope === 'excluded') return 'excluded only';
    if (statsScope === 'both') return 'included + excluded';
    return 'included only';
  }, [statsScope]);

  const selectStatsScope = useCallback((scope) => {
    const next = scope === 'excluded' || scope === 'both' ? scope : 'included';
    setStatsScope(next);
    try {
      localStorage.setItem(STATS_SCOPE_KEY, next);
    } catch { /* ignore */ }
  }, []);

  // Averages + subject ranks follow selected scope (included / excluded / both)
  const subjectStats = useMemo(
    () => computeSubjectStats(statsRows, SUBJECT_MAX, CLASS9_KEYS, CLASS10_KEYS),
    [statsRows, SUBJECT_MAX, CLASS9_KEYS, CLASS10_KEYS]
  );

  const duplicateRolls = useMemo(() => {
    const counts = new Map();
    for (const r of displayRows) {
      const roll = String(r.roll_no || '').trim();
      if (!roll) continue;
      counts.set(roll, (counts.get(roll) || 0) + 1);
    }
    const dups = new Set();
    for (const [roll, n] of counts) {
      if (n > 1) dups.add(roll);
    }
    return dups;
  }, [displayRows]);

  const toggleColumn = (key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      for (const k of ['rank_no', 'roll_no', 'student_name', 'section_name']) next.delete(k);
      localStorage.setItem(colVisStorageKey(groupKind, part), JSON.stringify([...next]));
      return next;
    });
  };

  const showAllColumns = () => {
    setHiddenColumns(new Set());
    localStorage.setItem(colVisStorageKey(groupKind, part), JSON.stringify([]));
  };

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  useEffect(() => {
    setHiddenColumns(loadHiddenColumns(groupKind, part, isCombined));
  }, [part, groupKind, isCombined]);

  useEffect(() => {
    if (!showColumnsMenu) return undefined;
    const onDoc = (e) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) {
        setShowColumnsMenu(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showColumnsMenu]);

  useEffect(() => {
    const syncFs = () => {
      setTableFullscreen(
        !!document.fullscreenElement
        && document.fullscreenElement === tableCardRef.current
      );
    };
    document.addEventListener('fullscreenchange', syncFs);
    return () => document.removeEventListener('fullscreenchange', syncFs);
  }, []);

  const toggleTableFullscreen = useCallback(async () => {
    const el = tableCardRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
        await el.requestFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (err) {
      console.error('[BoardResults] fullscreen', err);
      showMessage('Fullscreen is not available in this browser.', 'error');
    }
  }, []);

  const selectSection = useCallback((id) => {
    setSelectedSectionId(id);
    saveSelectedSectionId(year, level, part, id);
  }, [year, level, part]);

  const toggleCombineSection = useCallback((sectionId) => {
    const n = Number(sectionId);
    if (!Number.isFinite(n) || n <= 0) return;
    setCombineSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(n)) {
        if (next.size <= 1) return prev; // keep at least one
        next.delete(n);
      } else {
        next.add(n);
      }
      saveCombineIds(year, level, part, next);
      return next;
    });
  }, [year, level, part]);

  const selectAllCombineSections = useCallback(() => {
    const all = new Set(sections.map((s) => Number(s.id)).filter((n) => Number.isFinite(n) && n > 0));
    setCombineSectionIds(all);
    saveCombineIds(year, level, part, all);
  }, [sections, year, level, part]);

  const loadSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const qs = new URLSearchParams({
        year: String(year),
        level,
        part
      });
      const res = await fetch(`${API_BASE_URL}/board-results/sections?${qs}`, {
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load sections');
      const list = data.sections || [];
      setSections(list);
      if (data.combined) setCombinedMeta(data.combined);

      const available = list.map((s) => Number(s.id)).filter((n) => Number.isFinite(n) && n > 0);
      setCombineSectionIds((prev) => {
        const saved = loadSavedCombineIds(year, level, part);
        let next = [];
        if (saved?.length) {
          next = saved.filter((id) => available.includes(id));
        } else if (prev.size) {
          next = [...prev].map(Number).filter((id) => available.includes(id));
        }
        if (!next.length) next = available;
        const set = new Set(next);
        saveCombineIds(year, level, part, set);
        return set;
      });

      setSelectedSectionId((prev) => {
        const saved = loadSavedSectionId(year, level, part);
        const preferred = saved || prev;
        if (String(preferred) === COMBINED_ID) {
          saveSelectedSectionId(year, level, part, COMBINED_ID);
          return COMBINED_ID;
        }
        const exists = list.some((s) => String(s.id) === String(preferred));
        if (exists) {
          saveSelectedSectionId(year, level, part, preferred);
          return preferred;
        }
        const fallback = list[0]?.id != null ? list[0].id : COMBINED_ID;
        saveSelectedSectionId(year, level, part, fallback);
        return fallback;
      });
    } catch (err) {
      console.error('[BoardResults] sections', err);
      showMessage(err.message || 'Failed to load sections', 'error');
    } finally {
      setSectionsLoading(false);
    }
  }, [API_BASE_URL, authHeaders, year, level, part]);

  const loadRows = useCallback(async (opts = {}) => {
    if (selectedSectionId == null) return;
    const kind = opts.groupKind ?? groupKind;
    setListLoading(true);
    try {
      const qs = new URLSearchParams({
        year: String(year),
        level,
        part,
        sectionId: String(selectedSectionId),
        groupKind: kind
      });
      if (String(selectedSectionId) === COMBINED_ID && combineSectionIds.size > 0) {
        qs.set('sectionIds', [...combineSectionIds].join(','));
      }
      const res = await fetch(`${API_BASE_URL}/board-results?${qs}`, {
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load board results');
      setRows(data.rows || []);
    } catch (err) {
      console.error('[BoardResults] list', err);
      showMessage(err.message || 'Failed to load board results', 'error');
    } finally {
      setListLoading(false);
    }
  }, [API_BASE_URL, authHeaders, year, level, part, selectedSectionId, groupKind, combineSectionIds]);

  /** After a mutation: Combined view must reload full merge; section view can use returned rows. */
  const applyRowsOrReload = useCallback(async (dataRows) => {
    if (isCombined) {
      await loadRows();
      await loadSections();
      return;
    }
    if (Array.isArray(dataRows)) setRows(dataRows);
    else await loadRows();
    await loadSections();
  }, [isCombined, loadRows, loadSections]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    if (sectionsLoading) return;
    loadRows();
  }, [loadRows, sectionsLoading]);

  const canExtract = useMemo(
    () => {
      if (isCombined || !selectedSection || parsedRolls.length === 0 || loading) return false;
      if (part !== 'Part-I' && part !== 'Part-II') return false;
      if (level !== 'Matric' && level !== 'Intermediate') return false;
      if (biseSource === 'www') return wwwSourceSupported(level, part);
      return true;
    },
    [isCombined, selectedSection, parsedRolls, loading, part, level, biseSource]
  );

  const extractOne = async (roll) => {
    const res = await fetch(`${API_BASE_URL}/board-results/extract`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders()
      },
      body: JSON.stringify({
        rollNo: roll,
        part,
        year,
        level,
        sectionId: selectedSectionId,
        groupKind,
        captchaModel: CAPTCHA_MODEL,
        biseSource
      })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      let msg = data.message || 'Extraction failed';
      if (Array.isArray(data.attempts) && data.attempts.length) {
        const last = data.attempts[data.attempts.length - 1];
        if (last?.error && !msg.includes(last.error)) {
          msg = `${msg} | ${last.error}`;
        }
      }
      throw new Error(msg);
    }
    return data;
  };

  const handleExtractAll = async () => {
    const rolls = parseRollList(rollListText);
    if (!rolls.length) {
      showMessage('Paste at least one roll number.', 'error');
      return;
    }
    if (isCombined) {
      showMessage('Select a section (e.g. Fast or TPS) before extracting. Combined is view-only.', 'error');
      return;
    }
    if (!selectedSection) {
      showMessage('Create or select a section first.', 'error');
      return;
    }
    if ((level !== 'Matric' && level !== 'Intermediate') || (part !== 'Part-II' && part !== 'Part-I')) {
      showMessage('Board extraction is for Matric / Intermediate Part-I and Part-II only.', 'error');
      return;
    }
    if (biseSource === 'www' && !wwwSourceSupported(level, part)) {
      showMessage(
        `biselahore.com JSON does not yet publish ${level} ${part} — switch Source to result.biselahore.com.`,
        'error'
      );
      return;
    }

    stopRef.current = false;
    setLoading(true);
    setBatchLog([]);
    setProgress({ done: 0, total: rolls.length, current: rolls[0], ok: 0, fail: 0 });

    let ok = 0;
    let fail = 0;
    const log = [];
    let reloadGroupKind = groupKind;

    for (let i = 0; i < rolls.length; i++) {
      if (stopRef.current) {
        setStatus(`Stopped after ${i} of ${rolls.length}.`);
        break;
      }

      const roll = rolls[i];
      setProgress({ done: i, total: rolls.length, current: roll, ok, fail });
      setStatus(`Extracting ${i + 1}/${rolls.length}: roll ${roll}…`);

      try {
        const data = await extractOne(roll);
        const saved = data.boardRow;
        const inView = Array.isArray(data.rows)
          && data.rows.some((r) => String(r.roll_no) === String(roll));
        if (!saved?.id) {
          throw new Error('Extract succeeded but row was not saved to this section.');
        }

        if (data.viewGroupKind && data.viewGroupKind !== reloadGroupKind) {
          reloadGroupKind = data.viewGroupKind;
          selectGroupKind(data.viewGroupKind);
        }
        if (isPostersView) {
          selectViewMode('table');
        }

        if (Array.isArray(data.rows) && data.rows.length) {
          setRows(data.rows);
        }

        ok += 1;
        log.push({
          roll,
          ok: true,
          message: `${saved?.student_name || roll} — ${saved?.group_name || '?'} — ${saved?.total_marks || '?'} (${saved?.percentage_label || '?'})`
        });
        const savedHum = /human/i.test(String(saved?.group_name || ''));
        if ((groupKind === 'humanities') !== savedHum) {
          log[log.length - 1].message += savedHum
            ? ' · shown under Humanities tab'
            : ' · shown under Science tab';
        }
        if (!inView && Array.isArray(data.rows) && data.rows.length) {
          log[log.length - 1].message += ' · table refreshed';
        }
      } catch (err) {
        fail += 1;
        log.push({ roll, ok: false, message: err.message || 'Failed' });
        console.error('[BoardResults] batch item', roll, err);
      }

      setBatchLog([...log]);
      setProgress({ done: i + 1, total: rolls.length, current: roll, ok, fail });
    }

    setLoading(false);
    await loadSections();
    await loadRows({ groupKind: reloadGroupKind });
    const stopped = stopRef.current;
    setStatus(
      stopped
        ? `Stopped — ${ok} saved, ${fail} failed.`
        : `Done — ${ok} saved, ${fail} failed out of ${rolls.length}.`
    );
    const firstFail = log.find((i) => !i.ok);
    const failHint = firstFail
      ? ` First error (roll ${firstFail.roll}): ${firstFail.message}`
      : '';
    showMessage(
      stopped
        ? `Batch stopped. Saved ${ok}, failed ${fail}.${failHint}`
        : `Batch complete. Saved ${ok}, failed ${fail} of ${rolls.length}.${failHint}`,
      fail && !ok ? 'error' : fail ? 'info' : 'success'
    );
  };

  const handleStop = () => {
    stopRef.current = true;
    setStatus('Stopping after current roll…');
  };

  const handleOpenBiseCard = async (row) => {
    if (!row?.id || !row?.roll_no) return;
    const win = window.open('', '_blank');
    if (!win) {
      showMessage('Pop-up blocked — allow pop-ups to open BISE result.', 'error');
      return;
    }
    try {
      win.document.write(
        `<!doctype html><html><head><title>Loading BISE…</title></head>
         <body style="font-family:system-ui,sans-serif;padding:2rem;color:#334155">
           <p>Loading BISE Lahore result for roll <strong>${String(row.roll_no)}</strong>…</p>
           <p style="font-size:0.9rem;color:#64748b">First open may take a few seconds if the card is not cached yet.</p>
         </body></html>`
      );
      win.document.close();
    } catch {
      /* ignore */
    }

    try {
      const qs = new URLSearchParams();
      if (!row.has_result_html) qs.set('refresh', '1');
      if (CAPTCHA_MODEL) qs.set('captchaModel', CAPTCHA_MODEL);
      const qstr = qs.toString() ? `?${qs.toString()}` : '';
      const res = await fetch(`${API_BASE_URL}/board-results/${row.id}/bise-card${qstr}`, {
        headers: authHeaders()
      });
      const html = await res.text();
      if (!res.ok) {
        let msg = 'Failed to open BISE result';
        try {
          const data = JSON.parse(html);
          if (data.message) msg = data.message;
        } catch {
          /* html error page */
        }
        throw new Error(msg);
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      // Mark as cached locally so next click is instant
      if (!row.has_result_html) {
        setRows((list) =>
          list.map((r) => (r.id === row.id ? { ...r, has_result_html: true } : r))
        );
      }
    } catch (err) {
      console.error('[BoardResults] bise card', err);
      try {
        win.document.open();
        win.document.write(
          `<!doctype html><html><body style="font-family:sans-serif;padding:2rem">
            <h2>Could not open BISE result</h2>
            <p>${String(err.message || 'Unknown error').replace(/</g, '&lt;')}</p>
            <p><a href="https://result.biselahore.com/" target="_blank" rel="noreferrer">Open result.biselahore.com</a></p>
          </body></html>`
        );
        win.document.close();
      } catch {
        /* ignore */
      }
      showMessage(err.message || 'Failed to open BISE result', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/board-results/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');
      await applyRowsOrReload(data.rows);
    } catch (err) {
      showMessage(err.message || 'Delete failed', 'error');
    }
  };

  const handleToggleDisabled = async (row) => {
    if (!row?.id) return;
    const nextDisabled = !row.is_disabled;
    try {
      const res = await fetch(`${API_BASE_URL}/board-results/${row.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({ is_disabled: nextDisabled })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Update failed');
      await applyRowsOrReload(data.rows);
      setSelectedIds((prev) => {
        if (!prev.has(row.id)) return prev;
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      showMessage(
        nextDisabled
          ? `${row.student_name || row.roll_no} excluded from list / averages / ranking.`
          : `${row.student_name || row.roll_no} included again.`,
        'success'
      );
    } catch (err) {
      showMessage(err.message || 'Failed to update student', 'error');
    }
  };

  const toggleRowSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllInView = () => {
    setSelectedIds((prev) => {
      if (allSelectedInView) {
        const next = new Set(prev);
        for (const id of displayIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of displayIds) next.add(id);
      return next;
    });
  };

  const handleBulkDisabled = async () => {
    const ids = displayIds.filter((id) => selectedIds.has(id));
    if (!ids.length) {
      showMessage('Select at least one student.', 'info');
      return;
    }
    const nextDisabled = !showDisabled;
    const verb = nextDisabled ? 'Hide' : 'Include';
    showConfirm(
      `${verb} ${ids.length} selected student(s)?`,
      async () => {
        setBulkBusy(true);
        try {
          const res = await fetch(`${API_BASE_URL}/board-results/bulk-disabled`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders()
            },
            body: JSON.stringify({ ids, is_disabled: nextDisabled })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(data.message || 'Bulk update failed');
          await applyRowsOrReload(data.rows);
          setSelectedIds(new Set());
          showMessage(
            nextDisabled
              ? `${ids.length} student(s) hidden from list / averages / ranking.`
              : `${ids.length} student(s) included again.`,
            'success'
          );
        } catch (err) {
          showMessage(err.message || 'Bulk update failed', 'error');
        } finally {
          setBulkBusy(false);
        }
      }
    );
  };

  const handleBulkDelete = () => {
    const ids = displayIds.filter((id) => selectedIds.has(id));
    if (!ids.length) {
      showMessage('Select at least one student.', 'info');
      return;
    }
    showConfirm(
      `Permanently delete ${ids.length} selected student(s)? This cannot be undone.`,
      async () => {
        setBulkBusy(true);
        try {
          const res = await fetch(`${API_BASE_URL}/board-results/bulk-delete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders()
            },
            body: JSON.stringify({
              ids,
              sectionId: selectedSectionId,
              groupKind
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(data.message || 'Bulk delete failed');
          setSelectedIds(new Set());
          await applyRowsOrReload(data.rows);
          showMessage(`Deleted ${ids.length} student(s).`, 'success');
        } catch (err) {
          showMessage(err.message || 'Bulk delete failed', 'error');
        } finally {
          setBulkBusy(false);
        }
      }
    );
  };

  const handleExportExcel = async () => {
    if (!activeRows.length && !disabledRows.length) {
      showMessage('No rows to export.', 'info');
      return;
    }
    setExporting(true);
    try {
      const sectionLabel = isCombined
        ? `Combined_${combinedNamesLabel}_${isHumanitiesView ? 'Humanities' : 'Science'}`
        : `${selectedSection?.name || 'Section'}_${isHumanitiesView ? 'Humanities' : 'Science'}`;
      await exportBoardResultsExcel({
        columns: COLUMNS,
        activeRows: activeRows.map((r) => ({
          ...r,
          marks_diff: formatMarksDiff(computeMarksDiff(r))
        })),
        disabledRows: disabledRows.map((r) => ({
          ...r,
          marks_diff: formatMarksDiff(computeMarksDiff(r))
        })),
        subjectStats,
        subjectMax: SUBJECT_MAX,
        level,
        part,
        year,
        sectionName: sectionLabel,
        statsScopeLabel
      });
      showMessage('Excel downloaded.', 'success');
    } catch (err) {
      console.error('[BoardResults] export', err);
      showMessage(err.message || 'Excel export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleClearAll = () => {
    if (isCombined) {
      showMessage('Select a section to clear. Combined is view-only.', 'info');
      return;
    }
    if (!rows.length) {
      showMessage('No rows to clear.', 'info');
      return;
    }
    const sectionName = selectedSection?.name || 'this section';
    const groupLabel = isHumanitiesView ? 'Humanities' : 'Science';
    showConfirm(
      `Clear all ${rows.length} ${groupLabel} row(s) in “${sectionName}” for ${level} · ${part} · ${year}? This cannot be undone.`,
      async () => {
        try {
          const qs = new URLSearchParams({
            year: String(year),
            level,
            part,
            sectionId: String(selectedSectionId),
            groupKind
          });
          const res = await fetch(`${API_BASE_URL}/board-results?${qs}`, {
            method: 'DELETE',
            headers: authHeaders()
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(data.message || 'Clear failed');
          setRows([]);
          setBatchLog([]);
          setProgress(null);
          setStatus('');
          await loadSections();
          showMessage(`Cleared ${groupLabel} rows in “${sectionName}”.`, 'success');
        } catch (err) {
          showMessage(err.message || 'Failed to clear rows', 'error');
        }
      }
    );
  };

  const handleCreateSection = () => {
    const name = window.prompt('New section name (e.g. Fast, TPS):');
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      showMessage('Section name is required.', 'info');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/board-results/sections`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
          },
          body: JSON.stringify({ name: trimmed, year, level, part })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.message || 'Create failed');
        setSections(data.sections || []);
        if (data.section?.id != null) selectSection(data.section.id);
        showMessage(`Section “${trimmed}” created.`, 'success');
      } catch (err) {
        showMessage(err.message || 'Failed to create section', 'error');
      }
    })();
  };

  const handleRenameSection = (section) => {
    if (!section?.id) return;
    const name = window.prompt('Rename section:', section.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === section.name) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/board-results/sections/${section.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders()
          },
          body: JSON.stringify({ name: trimmed })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.message || 'Rename failed');
        setSections(data.sections || []);
        showMessage(`Renamed to “${trimmed}”.`, 'success');
      } catch (err) {
        showMessage(err.message || 'Failed to rename section', 'error');
      }
    })();
  };

  const handleDeleteSection = (section) => {
    if (!section?.id) return;
    if (!canDeleteSection) {
      showMessage('Only an administrator can delete a section.', 'error');
      return;
    }
    showConfirm(
      `Delete section “${section.name}” and all ${section.student_count || 0} student row(s) in it? This cannot be undone.`,
      async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/board-results/sections/${section.id}`, {
            method: 'DELETE',
            headers: authHeaders()
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');
          const list = data.sections || [];
          setSections(list);
          if (String(selectedSectionId) === String(section.id)) {
            const next = list[0]?.id != null ? list[0].id : COMBINED_ID;
            selectSection(next);
          }
          showMessage(`Section “${section.name}” deleted.`, 'success');
        } catch (err) {
          showMessage(err.message || 'Failed to delete section', 'error');
        }
      }
    );
  };

  const handleCellSave = async (row, key, rawValue) => {
    if (READONLY_KEYS.has(key) || !row?.id) return;
    const prev = row[key] == null ? '' : String(row[key]);
    const next = String(rawValue ?? '').trim();
    if (prev === next) return;

    setRows((list) => list.map((r) => (r.id === row.id ? { ...r, [key]: next || null } : r)));

    try {
      const res = await fetch(`${API_BASE_URL}/board-results/${row.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({ [key]: next === '' ? null : next })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      await applyRowsOrReload(data.rows);
    } catch (err) {
      console.error('[BoardResults] save', err);
      showMessage(err.message || 'Failed to save cell', 'error');
      await loadRows();
    }
  };

  const pctComplete = progress?.total
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div className="board-results">
      <div className="br-topbar">
        <span className="br-title">
          <FontAwesomeIcon icon={faTrophy} />
          Board Results
        </span>

        <span className="br-field">
          <label htmlFor="br-level">Level</label>
          <select
            id="br-level"
            value={level}
            onChange={(e) => {
              const next = e.target.value;
              setLevel(next);
            }}
            disabled={loading}
          >
            <option value="Matric">Matric (SSC)</option>
            <option value="Intermediate">Intermediate (HSSC)</option>
          </select>
        </span>

        <span className="br-field">
          <label htmlFor="br-part">Exam</label>
          <select
            id="br-part"
            value={part}
            onChange={(e) => setPart(e.target.value)}
            disabled={loading}
          >
            <option value="Part-I">Part-I (ANNUAL)</option>
            <option value="Part-II">Part-II (ANNUAL)</option>
            <option value="Supplementary">Supplementary</option>
          </select>
        </span>

        <span className="br-field">
          <label htmlFor="br-year">Year</label>
          <select
            id="br-year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            disabled={loading}
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </span>

        <span className="br-field br-field-source">
          <label htmlFor="br-source">Source</label>
          <select
            id="br-source"
            value={biseSource}
            onChange={(e) => {
              const next = e.target.value === 'www' ? 'www' : 'result';
              if (next === 'www' && !wwwSourceSupported(level, part)) {
                showMessage(
                  `biselahore.com JSON does not yet publish ${level} ${part} — using result.biselahore.com.`,
                  'info'
                );
                setBiseSource('result');
                saveBiseSource('result');
                return;
              }
              setBiseSource(next);
              saveBiseSource(next);
            }}
            disabled={loading}
            title={BISE_SOURCES.find((s) => s.key === biseSource)?.title || ''}
          >
            {BISE_SOURCES.map((s) => (
              <option key={s.key} value={s.key} title={s.title}>
                {s.label}
              </option>
            ))}
          </select>
        </span>

        <span className="br-field br-field-rolls">
          <label htmlFor="br-rolls">Rolls</label>
          {/* Collapsed to one line at rest; grows on focus so pasting a long
              list stays reviewable without permanently eating table height. */}
          <textarea
            id="br-rolls"
            className="br-rolls"
            rows={1}
            placeholder="Paste roll numbers — new lines, spaces or commas"
            value={rollListText}
            onChange={(e) => setRollListText(e.target.value)}
            disabled={loading}
          />
          {parsedRolls.length > 0 && (
            <span className="br-rolls-count" title={`${parsedRolls.length} unique roll number(s)`}>
              {parsedRolls.length}
            </span>
          )}
        </span>

        <button
          type="button"
          className="board-results-btn primary board-results-btn-sm"
          disabled={!canExtract}
          onClick={handleExtractAll}
          title={
            isCombined
              ? 'Select a section first — Combined is view-only'
              : biseSource === 'www' && !wwwSourceSupported(level, part)
                ? `biselahore.com JSON does not yet publish ${level} ${part} — switch Source to result.biselahore.com`
                : (level === 'Matric' || level === 'Intermediate')
                  && (part === 'Part-II' || part === 'Part-I')
                  ? `Save into “${selectedSection?.name || 'section'}” via ${
                    biseSource === 'www' ? 'biselahore.com' : 'result.biselahore.com'
                  }`
                  : 'Board extraction is Matric / Intermediate Part-I and Part-II only'
          }
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Extracting…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSearch} />
              {parsedRolls.length > 1 ? ` Extract ${parsedRolls.length}` : ' Extract'}
              {selectedSection ? ` → ${selectedSection.name}` : ''}
            </>
          )}
        </button>

        {loading && (
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            onClick={handleStop}
          >
            <FontAwesomeIcon icon={faStop} /> Stop
          </button>
        )}
      </div>

      <div className="br-scopebar">
        <div className="br-segment" role="tablist" aria-label="Result group">
          <button
            type="button"
            role="tab"
            aria-selected={!isHumanitiesView && !isPostersView}
            className={`br-segment-btn${!isHumanitiesView && !isPostersView ? ' is-active' : ''}${isPostersView && !isHumanitiesView ? ' is-filter-on' : ''}`}
            onClick={() => selectGroupKind('science')}
            disabled={loading}
            title="Science subject table (Math / Phy / Chem / Bio)"
          >
            Science
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isHumanitiesView && !isPostersView}
            className={`br-segment-btn${isHumanitiesView && !isPostersView ? ' is-active' : ''}${isPostersView && isHumanitiesView ? ' is-filter-on' : ''}`}
            onClick={() => selectGroupKind('humanities')}
            disabled={loading}
            title="Humanities subject table (Gen Math / Gen Sci / Adv Islamic)"
          >
            Humanities
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isPostersView}
            className={`br-segment-btn br-segment-posters${isPostersView ? ' is-active' : ''}`}
            onClick={() => selectViewMode('posters')}
            disabled={loading}
            title="Achievement posters for students scoring 75% or above"
          >
            <FontAwesomeIcon icon={faImage} /> Posters ≥75%
          </button>
        </div>

        <span className="br-scope-divider" aria-hidden="true" />

        <div className="br-sections" role="tablist" aria-label="Section">
          {sections.map((s) => {
            const active = String(selectedSectionId) === String(s.id);
            const count = isHumanitiesView ? s.humanities_count : s.science_count;
            return (
              <span
                key={s.id}
                role="tab"
                tabIndex={0}
                aria-selected={active}
                className={`br-section-pill${active ? ' is-active' : ''}`}
                title={`${s.name} — ${s.student_count || 0} total · Sci ${s.science_count || 0} · Hum ${s.humanities_count || 0}`}
                onClick={() => selectSection(s.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectSection(s.id);
                  }
                }}
              >
                {s.name}
                <span className="br-section-count">{count || 0}</span>
                <span className="br-section-tools" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="br-section-tool"
                    title="Rename section"
                    onClick={() => handleRenameSection(s)}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  {canDeleteSection && (
                    <button
                      type="button"
                      className="br-section-tool danger"
                      title="Delete section and all rows in it"
                      onClick={() => handleDeleteSection(s)}
                      disabled={loading || sections.length <= 1}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </span>
              </span>
            );
          })}

          <span
            role="tab"
            tabIndex={0}
            aria-selected={isCombined}
            className={`br-section-pill br-section-combined${isCombined ? ' is-active' : ''}`}
            title={
              isCombined
                ? `Merged view of ${combinedNamesLabel} — choose sections below · view-only`
                : 'Merge selected sections — read-only, pick a section to extract or clear'
            }
            onClick={() => selectSection(COMBINED_ID)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                selectSection(COMBINED_ID);
              }
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} />
            Combined
            <span className="br-section-count">{combinedDisplayCount}</span>
          </span>

          <button
            type="button"
            className="br-section-add"
            onClick={handleCreateSection}
            disabled={loading || sectionsLoading}
            title="Create a named section (Fast, TPS, …)"
          >
            <FontAwesomeIcon icon={faPlus} /> New
          </button>
        </div>

        {isCombined && (
          <div className="br-combine-pick" role="group" aria-label="Sections to combine">
            <span className="br-combine-label">Merge</span>
            {sections.map((s) => {
              const on = combineSectionIds.has(Number(s.id));
              const count = isHumanitiesView ? s.humanities_count : s.science_count;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`br-combine-chip${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  disabled={loading || listLoading}
                  title={on ? `Remove ${s.name} from Combined` : `Add ${s.name} to Combined`}
                  onClick={() => toggleCombineSection(s.id)}
                >
                  {s.name}
                  <span className="br-section-count">{count || 0}</span>
                </button>
              );
            })}
            {sections.length > 1 && combineSectionIds.size < sections.length && (
              <button
                type="button"
                className="br-combine-all"
                disabled={loading || listLoading}
                onClick={selectAllCombineSections}
                title="Include every section"
              >
                All
              </button>
            )}
            <span className="br-scope-hint">View-only — pick a section to extract.</span>
          </div>
        )}
      </div>

      {(status || progress) && (
        <div className="br-status" role="status">
          {loading && <FontAwesomeIcon icon={faSpinner} spin />}
          <span className="br-status-text">{status}</span>
          {progress && (
            <>
              <span className="board-results-progress-bar">
                <span style={{ width: `${pctComplete}%` }} />
              </span>
              <span className="muted">
                {progress.done}/{progress.total}
                {progress.ok || progress.fail
                  ? ` · ok ${progress.ok} · fail ${progress.fail}`
                  : ''}
              </span>
            </>
          )}
        </div>
      )}

      {batchLog.length > 0 && (
        <details className="br-log" open={batchLog.some((i) => !i.ok)}>
          <summary>
            Extraction log — {batchLog.filter((i) => i.ok).length} ok,{' '}
            {batchLog.filter((i) => !i.ok).length} failed
          </summary>
          <div className="br-log-body">
            {batchLog.map((item) => (
              <div key={item.roll} className={item.ok ? 'ok' : 'fail'}>
                <strong>{item.roll}</strong> — {item.message}
              </div>
            ))}
          </div>
        </details>
      )}

      <div
        className={`board-results-card br-table-card${tableFullscreen ? ' is-table-fullscreen' : ''}`}
        ref={tableCardRef}
      >
        <div className="board-results-table-toolbar">
          <div className="board-results-toolbar-left">
            <strong>
              {isPostersView
                ? `Posters · ${level} · ${part} · ${year}`
                : `${showDisabled ? 'Excluded — ' : ''}${level} · ${part} · ${year}`}
            </strong>
            <span className="muted board-results-summary">
              {listLoading || sectionsLoading ? (
                'Loading…'
              ) : isPostersView ? (
                <>
                  <FontAwesomeIcon icon={faImage} className="board-results-summary-icon" />
                  Students ≥75%
                  {isCombined
                    ? ` · Combined`
                    : selectedSection
                      ? ` · ${selectedSection.name}`
                      : ''}
                  {isHumanitiesView ? ' · Humanities' : ' · Sciences'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUsers} className="board-results-summary-icon" />
                  {showDisabled
                    ? `${disabledRows.length} excluded`
                    : `${activeRows.length}`}
                  {isCombined
                    ? ' Combined'
                    : selectedSection
                      ? ` ${selectedSection.name}`
                      : ''}
                  {!showDisabled && disabledRows.length > 0
                    ? ` · ${disabledRows.length} excluded`
                    : ''}
                  {duplicateRolls.size > 0 ? ` · ${duplicateRolls.size} dup` : ''}
                  {isHumanitiesView ? ' · Humanities' : ' · Sciences'}
                </>
              )}
            </span>
          </div>
          {isPostersView ? (
            <div className="board-results-toolbar-right">
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => selectViewMode('table')}
                title="Return to marks table"
              >
                ← Table
              </button>
            </div>
          ) : (
          <div className="board-results-toolbar-right">
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm board-results-btn-icon"
              onClick={toggleTableFullscreen}
              title={tableFullscreen ? 'Exit full window (Esc)' : 'Open table in full browser window'}
              aria-label={tableFullscreen ? 'Exit full window' : 'Full window'}
            >
              <FontAwesomeIcon icon={tableFullscreen ? faCompress : faExpand} />
            </button>
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm"
              onClick={handleExportExcel}
              disabled={exporting || listLoading || rows.length === 0}
              title="Download Excel with same colors (Humanities, duplicates, fail cells)"
            >
              <FontAwesomeIcon icon={exporting ? faSpinner : faFileExcel} spin={exporting} />
              {exporting ? ' …' : ' Export'}
            </button>
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm"
              onClick={handleBulkDisabled}
              disabled={bulkBusy || loading || selectedInView === 0}
              title={
                showDisabled
                  ? 'Include selected students in list / averages / ranking'
                  : 'Hide selected students from list / averages / ranking'
              }
            >
              <FontAwesomeIcon icon={bulkBusy ? faSpinner : showDisabled ? faUserCheck : faUserSlash} spin={bulkBusy} />
              {bulkBusy
                ? ' …'
                : showDisabled
                  ? ` Include (${selectedInView})`
                  : ` Hide (${selectedInView})`}
            </button>
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm board-results-btn-danger"
              onClick={handleBulkDelete}
              disabled={bulkBusy || loading || selectedInView === 0}
              title="Permanently delete selected students"
            >
              <FontAwesomeIcon icon={faTrash} />
              {` Delete (${selectedInView})`}
            </button>
            <button
              type="button"
              className={`board-results-btn secondary board-results-btn-sm${showDisabled ? ' is-active' : ''}`}
              onClick={() => setShowDisabled((v) => !v)}
              title={showDisabled ? 'Back to included students' : 'View excluded (absent) students'}
            >
              <FontAwesomeIcon icon={showDisabled ? faEyeSlash : faEye} />
              {showDisabled
                ? ' Show included'
                : ` Show excluded${disabledRows.length ? ` (${disabledRows.length})` : ''}`}
            </button>
            <div
              className="board-results-stats-scope"
              role="group"
              aria-label="Subject average and rank scope"
              title="Who to include in subject averages and ranks"
            >
              <span className="board-results-stats-scope-label">Rank</span>
              {STATS_SCOPES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`br-section-pill${statsScope === s.key ? ' is-active' : ''}`}
                  onClick={() => selectStatsScope(s.key)}
                  disabled={
                    (s.key === 'included' && activeRows.length === 0)
                    || (s.key === 'excluded' && disabledRows.length === 0)
                    || (s.key === 'both' && rows.length === 0)
                  }
                  title={s.title}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="board-results-btn secondary board-results-btn-sm board-results-btn-danger"
              onClick={handleClearAll}
              disabled={loading || listLoading || rows.length === 0 || isCombined}
              title={isCombined ? 'Select a section to clear' : 'Delete all rows in this section'}
            >
              <FontAwesomeIcon icon={faEraser} /> Clear all
            </button>
            <div className="board-results-columns-wrap" ref={columnsMenuRef}>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => setShowColumnsMenu((v) => !v)}
              >
                <FontAwesomeIcon icon={faColumns} /> Columns
              </button>
              {showColumnsMenu && (
                <div className="board-results-columns-menu">
                  <div className="board-results-columns-menu-head">
                    <span>Show / hide columns</span>
                    <button type="button" className="linkish" onClick={showAllColumns}>Show all</button>
                  </div>
                  <div className="board-results-columns-list">
                    {COLUMNS.map((c) => {
                      const label = c.label || '(spacer)';
                      const locked = ['rank_no', 'roll_no', 'student_name', 'section_name'].includes(c.key);
                      const checked = !hiddenColumns.has(c.key);
                      return (
                        <label key={c.key} className={locked ? 'locked' : ''}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            onChange={() => toggleColumn(c.key)}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
        {isPostersView ? (
          <BoardResultsPosters
            rows={rows}
            listLoading={listLoading}
            level={level}
            part={part}
            year={year}
            groupLabel={isHumanitiesView ? 'Humanities' : 'Science'}
            sectionLabel={
              isCombined
                ? `Combined (${combinedNamesLabel})`
                : selectedSection?.name || ''
            }
            apiBaseUrl={API_BASE_URL}
            authHeaders={authHeaders}
          />
        ) : (
        <div className="board-results-table-wrap wide sticky-ready">
          <table className="board-results-table sheet">
            <thead>
              <tr className="board-results-head-row">
                <th className="board-results-select-col col-freeze col-freeze-select">
                  <input
                    type="checkbox"
                    checked={allSelectedInView}
                    disabled={displayIds.length === 0 || listLoading}
                    onChange={toggleSelectAllInView}
                    title={allSelectedInView ? 'Deselect all' : 'Select all visible'}
                    aria-label="Select all visible students"
                  />
                </th>
                {visibleColumns.map((c) => {
                  const sortable = c.key && c.key !== 'column2';
                  const active = sortable && sortKey === c.key;
                  return (
                    <th
                      key={c.key || 'spacer'}
                      className={[
                        c.key === 'student_name' ? 'col-name' : '',
                        freezeColClass(c.key),
                        sortable ? 'col-sortable' : '',
                        active ? `is-sorted is-sorted-${sortDir}` : ''
                      ].filter(Boolean).join(' ') || undefined}
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      title={
                        sortable
                          ? active
                            ? `Sorted ${sortDir === 'asc' ? 'ascending' : 'descending'} — click to reverse`
                            : `Sort by ${c.label || c.key}`
                          : undefined
                      }
                      aria-sort={
                        active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      <span className="col-sort-label">
                        {c.label}
                        {sortable && (
                          <span className="col-sort-icon" aria-hidden>
                            {active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
                <th />
              </tr>
              {statsRows.length > 0 && (
                <>
                  <tr className="board-results-avg-row">
                    <th className="board-results-select-col col-freeze col-freeze-select" />
                    {visibleColumns.map((c) => {
                      if (c.key === 'student_name') {
                        return (
                          <th key={c.key} className={`col-name ${freezeColClass(c.key)}`.trim()}>
                            {`Subject avg % (${statsScopeLabel})`}
                          </th>
                        );
                      }
                      if (c.key === 'rank_no' || c.key === 'roll_no' || c.key === 'marks_diff') {
                        return (
                          <th
                            key={c.key}
                            className={freezeColClass(c.key) || undefined}
                          />
                        );
                      }
                      const avg = subjectStats.avgs[c.key];
                      return (
                        <th key={c.key} title={SUBJECT_MAX[c.key] != null ? `Max ${SUBJECT_MAX[c.key]}` : ''}>
                          {avg == null ? '' : `${avg.toFixed(1)}%`}
                        </th>
                      );
                    })}
                    <th />
                  </tr>
                  <tr className="board-results-subj-rank-row">
                    <th className="board-results-select-col col-freeze col-freeze-select" />
                    {visibleColumns.map((c) => {
                      if (c.key === 'student_name') {
                        return (
                          <th key={c.key} className={`col-name ${freezeColClass(c.key)}`.trim()}>
                            {`Subject rank 9th/10th (${statsScopeLabel})`}
                          </th>
                        );
                      }
                      if (c.key === 'rank_no' || c.key === 'roll_no' || c.key === 'marks_diff') {
                        return (
                          <th
                            key={c.key}
                            className={freezeColClass(c.key) || undefined}
                          />
                        );
                      }
                      const rank = subjectStats.ranks[c.key];
                      const group = CLASS9_KEYS.has(c.key)
                        ? '9th'
                        : CLASS10_KEYS.has(c.key)
                          ? '10th'
                          : null;
                      return (
                        <th
                          key={c.key}
                          title={rank != null && group ? `${group} subject rank ${rank} (${statsScopeLabel})` : undefined}
                        >
                          {rank == null ? '' : String(rank)}
                        </th>
                      );
                    })}
                    <th />
                  </tr>
                </>
              )}
            </thead>
            <tbody>
              {!listLoading && displayRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 2} style={{ textAlign: 'center', opacity: 0.7 }}>
                    {showDisabled
                      ? 'No excluded students.'
                      : isCombined
                        ? 'No rows in any section yet — open a section card and extract.'
                        : isHumanitiesView
                          ? 'No Humanities rows yet — paste rolls and extract (Gen Math / Gen Sci / Adv Isl).'
                          : 'No Science rows yet — paste roll numbers above and extract.'}
                  </td>
                </tr>
              )}
              {displayRows.map((r) => {
                const humanities = isHumanities(r.group_name);
                const dup = duplicateRolls.has(String(r.roll_no || '').trim());
                const isSelected = selectedIds.has(r.id);
                const rowClass = [
                  !isHumanitiesView && humanities ? 'row-humanities' : '',
                  dup ? 'row-duplicate-roll' : '',
                  r.is_disabled ? 'row-disabled' : '',
                  isSelected ? 'row-selected' : ''
                ].filter(Boolean).join(' ') || undefined;
                return (
                  <tr
                    key={r.id || r.roll_no}
                    className={rowClass}
                    title={
                      r.is_disabled
                        ? 'Excluded from averages & ranking'
                        : dup
                          ? `Duplicate roll number: ${r.roll_no}`
                          : humanities
                            ? `Group: ${r.group_name}`
                            : r.group_name || undefined
                    }
                  >
                    <td className="board-results-select-col col-freeze col-freeze-select">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!r.id || loading || bulkBusy}
                        onChange={() => toggleRowSelected(r.id)}
                        aria-label={`Select ${r.student_name || r.roll_no || 'student'}`}
                      />
                    </td>
                    {visibleColumns.map((c) => {
                      const fail = isFailCell(r[c.key], c.key, SUBJECT_MAX);
                      const pct = subjectPct(r[c.key], c.key, SUBJECT_MAX);
                      const editable = !READONLY_KEYS.has(c.key);
                      const isRoll = c.key === 'roll_no';
                      const diff = c.key === 'marks_diff' ? computeMarksDiff(r) : null;
                      const diffClass =
                        diff == null
                          ? ''
                          : diff < 0
                            ? 'cell-diff-down'
                            : diff > 0
                              ? 'cell-diff-up'
                              : 'cell-diff-flat';
                      return (
                        <td
                          key={c.key}
                          className={[
                            fail ? 'cell-fail' : '',
                            editable ? 'cell-editable' : '',
                            isRoll ? 'cell-roll-link' : '',
                            diffClass,
                            c.key === 'student_name' ? 'col-name' : '',
                            freezeColClass(c.key)
                          ].filter(Boolean).join(' ') || undefined}
                          title={
                            isRoll
                              ? 'Open BISE Lahore result in new tab'
                              : c.key === 'marks_diff'
                                ? diff == null
                                  ? '10th Marks − 9th Marks'
                                  : diff < 0
                                    ? `${Math.abs(diff)} marks lower in 10th than 9th`
                                    : diff > 0
                                      ? `${diff} marks higher in 10th than 9th`
                                      : 'Same marks in 9th and 10th'
                                : pct != null && SUBJECT_MAX[c.key] != null
                                  ? `${r[c.key]}/${SUBJECT_MAX[c.key]} = ${pct.toFixed(1)}%`
                                  : editable
                                    ? 'Click to edit'
                                    : undefined
                          }
                          contentEditable={editable && !loading}
                          suppressContentEditableWarning
                          onClick={
                            isRoll
                              ? (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleOpenBiseCard(r);
                                }
                              : undefined
                          }
                          onBlur={
                            editable
                              ? (e) => handleCellSave(r, c.key, e.currentTarget.textContent)
                              : undefined
                          }
                          onKeyDown={
                            editable
                              ? (e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }
                              : isRoll
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleOpenBiseCard(r);
                                    }
                                  }
                                : undefined
                          }
                          role={isRoll ? 'link' : undefined}
                          tabIndex={isRoll ? 0 : undefined}
                        >
                          {cellDisplay(cellValue(r, c.key))}
                        </td>
                      );
                    })}
                    <td className="board-results-row-actions">
                      <button
                        type="button"
                        className="board-results-icon-btn"
                        title="Preview achievement poster"
                        onClick={() => setPosterPreviewRow(r)}
                        disabled={loading || bulkBusy}
                      >
                        <FontAwesomeIcon icon={faImage} />
                      </button>
                      <button
                        type="button"
                        className="board-results-icon-btn include-btn"
                        title={r.is_disabled ? 'Include in list / averages / ranking' : 'Exclude (hide from averages & ranking)'}
                        onClick={() => handleToggleDisabled(r)}
                        disabled={loading || bulkBusy}
                      >
                        <FontAwesomeIcon icon={r.is_disabled ? faUserCheck : faUserSlash} />
                      </button>
                      <button
                        type="button"
                        className="board-results-icon-btn"
                        title="Delete row"
                        onClick={() => handleDelete(r.id)}
                        disabled={loading || bulkBusy}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {posterPreviewRow && (
        <BoardResultsPosterPreview
          row={posterPreviewRow}
          apiBaseUrl={API_BASE_URL}
          authHeaders={authHeaders}
          onClose={() => setPosterPreviewRow(null)}
        />
      )}
      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />
      <ConfirmDialog
        confirm={confirm}
        onConfirm={handleConfirmOk}
        onCancel={handleConfirmCancel}
      />
    </div>
  );
}
